import React, { useState, useEffect } from 'react';
import './Challenge.css';
import { CSVLink } from 'react-csv';
import axios from 'axios';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const CareerPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const token = localStorage.getItem('auth-token');

    const fetchApplications = async () => {
        setError('');
        setLoading(true);
        const apiEndpoint = process.env.REACT_APP_API_CAREER;
        const headers = {
            "x-authorization": process.env.REACT_APP_AUTH_TOKEN,
            'Content-Type': 'application/json',
        };

        try {
            const response = await axios.get(apiEndpoint, { headers });

            if (response.data.success) {
                const transformedApplications = response.data.data.applications.map(app => ({
                    id: app.id,
                    name: app.name || '-',
                    mobileNumber: app.mobileNumber || '-',
                    email: app.email || '-',
                    experience: app.experience || '0',
                    role: app.role || '-',
                    resume: app.resume || '-',
                    linkedInProfile: app.linkedInProfile || '-',
                    createdAt: app.createdAt || '-',
                    gender: app.gender || '-',
                }));

                setApplications(transformedApplications);
            } else {
                setError('Failed to load data: No applications available.');
            }
        } catch (error) {
            setError('Error fetching data: ' + error.message);
            console.error("Error fetching applications:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApplications();
    }, []);

    const headers = [
        { label: "Name", key: "name", isNumeric: false },
        { label: "Mobile", key: "mobileNumber", isNumeric: false },
        { label: "Email", key: "email", isNumeric: false },
        { label: "Gender", key: "gender", isNumeric: false },
        { label: "Experience (years)", key: "experience", isNumeric: true },
        { label: "Role", key: "role", isNumeric: false },
        { label: "Resume", key: "resume", isNumeric: false },
        { label: "LinkedIn", key: "linkedInProfile", isNumeric: false },
        { label: "Applied On", key: "createdAt", isNumeric: false },
    ];

    const [sortConfig, setSortConfig] = useState({
        key: 'createdAt',
        direction: 'desc',
        numericKeys: ['id', 'experience']
    });

    const handleItemsPerPageChange = (event) => {
        setRowsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const filteredApplications = applications.filter((app) =>
        Object.values(app)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortData = (data) => {
        const { key, direction } = sortConfig;
        return [...data].sort((a, b) => {
            if (key === 'experience' || key === 'id') {
                const numA = parseFloat(a[key]) || 0;
                const numB = parseFloat(b[key]) || 0;
                return direction === 'asc' ? numA - numB : numB - numA;
            }

            const valueA = String(a[key] || '').toLowerCase();
            const valueB = String(b[key] || '').toLowerCase();

            if (valueA < valueB) return direction === 'asc' ? -1 : 1;
            if (valueA > valueB) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const sortedApplications = sortData(filteredApplications);
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = sortedApplications.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(sortedApplications.length / rowsPerPage);

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
    };

    const generatePageNumbers = () => {
        const pages = [];
        const totalPagesToShow = 5;

        if (totalPages <= totalPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const renderPagination = () => {
        return generatePageNumbers().map((page, index) =>
            typeof page === 'number' ? (
                <button
                    key={index}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageClick(page)}
                >
                    {page}
                </button>
            ) : (
                <span key={index} className="pagination-dots">
                    {page}
                </span>
            )
        );
    };

    const totalEntries = filteredApplications.length;
    const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
    const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

    return (
        <div className="enrollment-container">
            <div className="search-bar">
                <div className="input-container">
                    <input
                        type="text"
                        id="searchInput"
                        placeholder=""
                        className={`search-input ${searchTerm ? "has-value" : ""}`}
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <label htmlFor="searchInput" className="floating-label">
                        🔍 Search
                    </label>
                </div>
                <CSVLink
                    data={filteredApplications.map(({ mobileNumber, ...rest }) => rest)}
                    headers={headers.filter(header => header.key !== 'mobileNumber')}
                    filename="CareerApplications.csv"
                    className="btn btn-primary no-decoration"
                >
                    <button className="csv_button" type="button">
                        <span className="button__text">
                            Export CSV
                        </span>
                        <span className="button__icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
                            </svg>
                        </span>
                    </button>
                </CSVLink>
            </div>

            <div className="items-and-dues">
                <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
                    <option value={10}>10 items per page</option>
                    <option value={50}>50 items per page</option>
                    <option value={75}>75 items per page</option>
                    <option value={100}>100 items per page</option>
                </select>
                <div className="pagination-selector">
                    <div className="button-group">
                        <div className="group-label">Items per page:</div>
                        <div className="button-options">
                            {[10, 50, 75, 100].map((option) => (
                                <button
                                    key={option}
                                    onClick={() =>
                                        handleItemsPerPageChange({ target: { value: option } })
                                    }
                                    className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                                >
                                    <span className="button-text">{option}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <button className="tt-action-btn" onClick={fetchApplications}>
                    <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {loading && (
                <div className="premium-loader">
                    <div className="progress-track">
                        <div className="progress-bar"></div>
                    </div>
                    <div className="status-text">
                        <span>L</span>
                        <span>O</span>
                        <span>A</span>
                        <span>D</span>
                        <span>I</span>
                        <span>N</span>
                        <span>G</span>
                    </div>
                </div>
            )}
            {error && <div className="error-message">{error}</div>}
            {!loading && !error && filteredApplications.length === 0 && (
                <div className="error-message">No applications available</div>
            )}

            <div className="tt-table-container">
                <table className="tt-table">
                    <thead>
                        <tr className="challenges-header-row">
                            {headers.map(({ key, label }) => (
                                <th
                                    key={key}
                                    className="challenges-column"
                                    onClick={() => handleSort(key)}
                                >
                                    <div className="th-content">
                                        <span>{label}</span>
                                        <FiChevronDown
                                            className={`sort-icon 
              ${sortConfig.key === key ? 'active' : ''} 
              ${sortConfig.key === key && sortConfig.direction === 'asc' ? 'asc' : ''} 
              ${sortConfig.key === key && sortConfig.direction === 'desc' ? 'desc' : ''}`}
                                        />
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentRows.map((app) => (
                            <tr key={app.id} className="challenges-row">
                                <td className="challenges-cell">{app.name}</td>
                                <td className="challenges-cell">{app.mobileNumber}</td>
                                <td className="challenges-cell">{app.email}</td>
                                <td className="challenges-cell">{app.gender}</td>
                                <td className="challenges-cell">{app.experience}</td>
                                <td className="challenges-cell">{app.role}</td>
                                <td className="challenges-cell">
                                    {app.resume !== '-' ? (
                                        <a
                                            href={app.resume}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="resume-download-link"
                                            title="View Resume"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#4CAF50">
                                                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5zm0 12.5c-2.48 0-4.5-2.02-4.5-4.5S9.52 8 12 8s4.5 2.02 4.5 4.5S14.48 17 12 17z" />
                                                <circle cx="12" cy="12" r="2.5" />
                                            </svg>
                                            <span>View</span>
                                        </a>
                                    ) : '-'}
                                </td>

                                <td className="challenges-cell">
                                    {app.linkedInProfile !== '-' ? (
                                        <a href={app.linkedInProfile} target="_blank" rel="noopener noreferrer" className="linkedin-link">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#0A66C2">
                                                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                            </svg>
                                            <span>Profile</span>
                                        </a>
                                    ) : '-'}
                                </td>
                                <td className="challenges-cell">{new Date(app.createdAt).toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <div className="showing-entries">
                    Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
                </div>
                <button
                    className="prev-button"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                >
                    Previous
                </button>
                {renderPagination()}
                <button
                    className="next-button"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default CareerPage;